import React, { useEffect, useState } from 'react';
import {
  Grid,
  Toolbar,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useUser, useUserDispatch } from '@/providers';
import { SET_USER } from '@/actions/types';

import { updateUser } from '../../../actions/user';
import { fetchEnvironments } from '../../../actions/environments';

const EnvironmentField = ({ name, label, value, onChange, setChanged }) => {
  const environments = useSelector((state) => state.environments);
  const environmentOptions = environments.map((env) => ({
    value: env.id,
    key: env.id,
    text: env.name,
  }));

  const handleChange = (event) => {
    const { value: newValue } = event.target;
    setChanged();
    onChange(newValue);
  };

  return (
    <Box>
      <FormControl fullWidth>
        <InputLabel>{label}</InputLabel>
        <Select
          name={name}
          value={value || ''}
          label={label}
          onChange={handleChange}
        >
          {environmentOptions.map((option) => (
            <MenuItem key={option.key} value={option.value}>
              {option.text}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default function DefaultSettingsForm() {
  const dispatch = useDispatch();
  const user = useUser();
  const userDispatch = useUserDispatch();

  const [pristine, setPristine] = useState(true);
  const [loading, setLoading] = useState(false);
  const [default_notebook_environment, setNotebookEnvironment] = useState(
    user?.default_notebook_environment,
  );
  const [default_training_environment, setTrainingEnvironment] = useState(
    user?.default_training_environment,
  );
  const [default_inference_environment, setInferenceEnvironment] = useState(
    user?.default_inference_environment,
  );
  const [default_endpoint_environment, setEndpointEnvironment] = useState(
    user?.default_endpoint_environment,
  );

  useEffect(() => {
    dispatch(fetchEnvironments());
  }, [dispatch]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const updatedUser = await updateUser({
        default_notebook_environment,
        default_training_environment,
        default_inference_environment,
        default_endpoint_environment,
      });
      userDispatch({ type: SET_USER, payload: updatedUser });
      setLoading(false);
      setPristine(true);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setNotebookEnvironment(user?.default_notebook_environment);
    setTrainingEnvironment(user?.default_training_environment);
    setInferenceEnvironment(user?.default_inference_environment);
    setEndpointEnvironment(user?.default_endpoint_environment);
    setPristine(true);
  };

  return (
    <Grid container spacing={2} sx={{ maxWidth: 600 }}>
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <EnvironmentField
          name='default_notebook_environment'
          label='Notebook Environment'
          value={default_notebook_environment}
          onChange={setNotebookEnvironment}
          setChanged={() => setPristine(false)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <EnvironmentField
          name='default_training_environment'
          label='Training Environment'
          value={default_training_environment}
          onChange={setTrainingEnvironment}
          setChanged={() => setPristine(false)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <EnvironmentField
          name='default_inference_environment'
          label='Inference Environment'
          value={default_inference_environment}
          onChange={setInferenceEnvironment}
          setChanged={() => setPristine(false)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <EnvironmentField
          name='default_endpoint_environment'
          label='Endpoint Environment'
          value={default_endpoint_environment}
          onChange={setEndpointEnvironment}
          setChanged={() => setPristine(false)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <Toolbar>
          <Stack spacing={2} direction='row'>
            <Button
              variant='contained'
              color='brand'
              onClick={handleSubmit}
              disabled={pristine || loading}
              startIcon={loading ? <CircularProgress size={20} /> : undefined}
            >
              Update
            </Button>
            <Button
              variant='outlined'
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Stack>
        </Toolbar>
      </Grid>
    </Grid>
  );
}
